import { Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService as AuthO, User } from "@auth0/auth0-angular";
import { Observable, Observer, switchMap } from "rxjs";
import { tap } from "rxjs/operators";
import { UserService } from "../services/user.service";
import { environment } from "../../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public isLoggedIn = false;
  public user: User = {};
  public accessToken: string = "";
  /** Separate token for the Wallet API (different Auth0 audience in production). */
  public walletAccessToken: string = "";
  /**
   * The signed-in user's effective Loans permissions, from the login bootstrap. Used to hide
   * actions the user may not take, such as "Add new supplier" (work item 789).
   */
  public readonly loansPermissions = signal<string[]>([]);
  /**
   * The signed-in user's Loans roles, from the same bootstrap. Only used to decide what to show,
   * such as the transaction log popup for Technical Staff (work item 704); the APIs enforce access.
   */
  public readonly loansRoles = signal<string[]>([]);

  private userObserver: Observer<User>;
  public user$: Observable<User> = new Observable<User>(
    (o) => (this.userObserver = o),
  );

  constructor(
    private auth0: AuthO,
    private router: Router,
    private userService: UserService
  ) {

    this.auth0.error$.subscribe((o: any) => {
      if (o.error === "missing_refresh_token") {
        // Don't redirect while on /login — we're still in the Auth0 callback
        // handshake. Redirecting here causes an infinite loop.
        if (!window.location.pathname.startsWith('/login')) {
          this.login(window.location.pathname);
        }
      } else {
        console.log(o);
      }
    });

    this.auth0.isAuthenticated$.subscribe((o) => {
      this.isLoggedIn = o;
      if (this.isLoggedIn) {
        this.auth0.getAccessTokenSilently().pipe(
          tap(token => {
            this.accessToken = token;
            // The Wallet API is configured to accept the same Loans-audience token,
            // so no separate wallet token acquisition is needed.
            this.walletAccessToken = token;
          }),
          switchMap(() => userService.login()),
          tap(user => {
            this.loansPermissions.set(user?.effectivePermissions ?? []);
            this.loansRoles.set(user?.roles ?? []);
          }),
          switchMap(() => userService.loginWallet())
        ).subscribe();

        this.auth0.user$.subscribe((u) => {
          this.user = u;
          this.userObserver.next(u);
        });
      }
    });
  }

  logout() {
    this.auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    this.isLoggedIn = false;
    this.user = {};
    this.accessToken = "";
    this.walletAccessToken = "";
    this.loansPermissions.set([]);
    this.loansRoles.set([]);
    this.userObserver.next(this.user);
  }

  login(redirectUri: string) {
    this.auth0.loginWithRedirect({ appState: { target: redirectUri } });
  }

  /**
   * Returns the correct Bearer token for the given request URL.
   * Wallet API calls use walletAccessToken; everything else uses accessToken.
   */
  getTokenForUrl(url: string): string {
    if (this.walletAccessToken && environment.apiWalletUrl && url.startsWith(environment.apiWalletUrl)) {
      return this.walletAccessToken;
    }
    return this.accessToken;
  }

  checkAuthState() {
    //this.auth0.handleRedirectCallback().subscribe(o => {
    //  this.router.navigateByUrl(o.appState.redirectUri);
    //});
  }
}
